import axios from 'axios';
import { baseUrl } from '../../Contants';

const axiosSRP = axios.create();

axiosSRP.interceptors.request.use(
  (config) => {
    const userData = localStorage.getItem('userDetails');
    const accessToken = localStorage.getItem('accessToken');
    const authType = localStorage.getItem('authType');

    if (userData && JSON.parse(userData)?.user_id) {
      config.headers.RequestorUserID = JSON.parse(userData)?.user_id;
    } else {
      config.headers.RequestorUserID = undefined;
    }

    config.headers.Authorization = `Bearer ${accessToken}`;
    config.headers.AuthType = authType;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Refresh is otherwise purely clock-driven (handleSessionCheck in AppContextProvide),
// which leaves the access token to die whenever that timer doesn't fire in time — a
// backgrounded tab, a refresh call that failed once, a grant revoked server-side. The
// server used to answer 500 for a rejected token, so there was no signal to act on
// either; now that it answers 401, recover from it here instead of replaying a dead
// token on every subsequent request.
let refreshPromise = null;

// These two must never trigger the refresh-and-retry path: /refresh *is* the recovery
// (and would recurse), and a 401 from /logout means the session is already gone.
const isAuthPlumbing = (url) =>
  !!url && (url.includes('/auth/logout') || url.includes('/refresh'));

async function refreshAccessToken() {
  const authType = localStorage.getItem('authType');
  const refreshToken = localStorage.getItem('refreshToken');

  // Only the OAuth logins hold a refresh grant. A `local` JWT has no renewal path, so
  // its 401 goes straight to logout.
  if (!refreshToken || (authType !== 'google' && authType !== 'microsoft')) {
    return null;
  }

  // Bare axios, not axiosSRP: this request must bypass the interceptor it is serving.
  const { data } = await axios.get(
    `${baseUrl}/api/auth/${authType}/refresh?refreshToken=${encodeURIComponent(refreshToken)}`
  );

  if (!data?.access_token) return null;

  const expiresIn = data.expires_at
    ? (Number(data.expires_at) - new Date().getTime()) / 1000
    : 3600;

  localStorage.setItem('accessToken', data.access_token);
  localStorage.setItem(
    'googleExpiration',
    new Date().getTime() + (expiresIn - 30) * 1000
  );
  // Microsoft rotates refresh tokens; persist the new one when returned.
  if (data.refresh_token) {
    localStorage.setItem('refreshToken', data.refresh_token);
  }

  return data.access_token;
}

// If the server is in a migration-failure brownout it returns 503 with
// { status: 'maintenance' }. An already-open SPA would otherwise just show
// broken UI, so signal a full-screen maintenance overlay (MaintenanceOverlay
// listens for this event). The error still rejects so callers behave normally.
axiosSRP.interceptors.response.use(
  (response) => response,
  async (error) => {
    const res = error && error.response;
    const config = error && error.config;

    if (res && res.status === 503 && res.data && res.data.status === 'maintenance') {
      window.dispatchEvent(new Event('srp-maintenance'));
    }

    if (
      res &&
      res.status === 401 &&
      config &&
      !config._retriedAfterRefresh &&
      !isAuthPlumbing(config.url)
    ) {
      // One attempt per request — a token the server still rejects after a fresh
      // refresh is not going to start working on the third try.
      config._retriedAfterRefresh = true;

      try {
        // Single-flight: a page load fires many requests at once and they would each
        // burn a refresh. That matters beyond wasted calls — Microsoft rotates refresh
        // tokens, so concurrent refreshes invalidate each other's grant.
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }

        const token = await refreshPromise;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return axiosSRP(config);
        }
      } catch (refreshError) {
        // Refresh itself was rejected (revoked grant, expired refresh token) — fall
        // through to logout.
      }

      // No refresh path, or the refresh failed: the session is genuinely over.
      // AppContextProvide listens for this and runs the normal logout.
      window.dispatchEvent(new Event('srp-unauthorized'));
    }

    return Promise.reject(error);
  }
);

export default axiosSRP;
